import { useState } from "react";
import { Package, Upload, Download, FileSpreadsheet, Search, Trash2, Image as ImageIcon, X, Plus, Pencil, Save } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import api, { readError } from "../api";
import { money, uploadFile } from "../studioUtils";

export default function ProductCatalog({ catalog, loadCatalog }) {
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const openNew = () => setSelected({ name: "", sku: "", default_price: "", price_currency: "USD", image_url: "" });
  const saveProduct = async () => {
    const name = selected.name.trim();
    if (!name) return toast.error("Ürün adı zorunlu");
    if (Number(selected.default_price) < 0) return toast.error("Fiyat negatif olamaz");
    if (!selected.originalName && catalog.some(p => p.name.toLocaleLowerCase("tr-TR") === name.toLocaleLowerCase("tr-TR")))
      return toast.error("Bu ürün zaten kayıtlı. Düzenle düğmesini kullanın.");
    setSaving(true);
    try {
      const body = { name, sku: selected.sku.trim(), default_price: Number(selected.default_price) || 0,
        price_currency: selected.price_currency, image_url: selected.image_url || "" };
      if (selected.originalName) await api.put(`/products/${encodeURIComponent(selected.originalName)}`, body);
      else await api.post("/products/bulk", { items: [body] });
      toast.success(selected.originalName ? "Ürün güncellendi" : "Ürün eklendi");
      setSelected(null); loadCatalog();
    } catch (err) { toast.error(readError(err, "Ürün kaydedilemedi")); }
    finally { setSaving(false); }
  };
  const uploadSelectedImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/") || file.size > 5 * 1024 * 1024) return toast.error("5 MB altında bir görsel seçin");
    try {
      const result = await uploadFile(file);
      setSelected(p => ({ ...p, image_url: result.absoluteUrl }));
    } catch (err) { toast.error(readError(err, "Görsel yüklenemedi")); }
  };

  const downloadTemplate = () => {
    const rows = [
      { "Ürün Adı": "IP Kamera 4MP", "Stok Kodu": "CAM-4MP-01", "Fiyat (USD)": 45 },
      { "Ürün Adı": "NVR Kayıt Cihazı 8 Kanal", "Stok Kodu": "NVR-8CH", "Fiyat (USD)": 120 },
      { "Ürün Adı": "PoE Switch 8 Port", "Stok Kodu": "SW-POE-8", "Fiyat (USD)": 75 },
    ];
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 40 }, { wch: 18 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ürünler");
    XLSX.writeFile(wb, "urun-sablonu.xlsx");
  };

  const exportCatalog = () => {
    if (!catalog.length) return toast.error("Katalogda ürün yok");
    const rows = catalog.map(p => ({ "Ürün Adı": p.name, "Stok Kodu": p.sku || "",
                                     "Fiyat": p.default_price || 0, "Para Birimi": p.price_currency || "USD" }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 40 }, { wch: 18 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ürünler");
    XLSX.writeFile(wb, "urun-katalogu.xlsx");
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
      const items = rows.map(r => ({
        name: String(r["Ürün Adı"] ?? r["name"] ?? r["Ürün"] ?? "").trim(),
        sku: String(r["Stok Kodu"] ?? r["SKU"] ?? r["sku"] ?? "").trim(),
        default_price: Number(r["Fiyat (USD)"] ?? r["Fiyat"] ?? r["price"] ?? 0) || 0,
        price_currency: String(r["Para Birimi"] || "USD").toUpperCase().replace(/^TL$/, "TRY"),
      })).filter(i => i.name);
      if (!items.length) return toast.error("Dosyada geçerli ürün yok");
      const { data } = await api.post("/products/bulk", { items });
      toast.success(`${data.inserted} yeni, ${data.updated} güncellendi`);
      loadCatalog();
    } catch (err) {
      toast.error(readError(err, "Excel okunamadı"));
    } finally {
      e.target.value = "";
    }
  };

  const removeProduct = async (name) => {
    if (!window.confirm(`"${name}" ürünü silinsin mi?`)) return;
    try {
      await api.delete(`/products/${encodeURIComponent(name)}`);
      toast.success("Ürün silindi");
      loadCatalog();
    } catch (err) { toast.error(readError(err)); }
  };

  const uploadProductImage = async (name, file) => {
    if (!file) return;
    try {
      const result = await uploadFile(file);
      await api.patch(`/products/${encodeURIComponent(name)}/image`, { image_url: result.absoluteUrl });
      toast.success("Ürün görseli güncellendi");
      loadCatalog();
    } catch (err) { toast.error(readError(err, "Görsel yüklenemedi")); }
  };

  const updateSku = async (name, sku) => {
    try {
      await api.patch(`/products/${encodeURIComponent(name)}/sku`, { sku });
      loadCatalog();
    } catch (err) { toast.error(readError(err, "SKU güncellenemedi")); }
  };

  const filtered = catalog.filter(p => !filter ||
    p.name.toLowerCase().includes(filter.toLowerCase()) ||
    (p.sku || "").toLowerCase().includes(filter.toLowerCase()));

  return (
    <section className="catalog-view">
      <div className="catalog-intro">
        <div className="settings-icon"><Package size={22} /></div>
        <div>
          <p className="eyebrow blue">ORTAK ÜRÜN HAFIZASI</p>
          <h2>Ürün kataloğu</h2>
          <p>Excel ile toplu ekleyin, ürünlere görsel iliştirin. Buradaki ürünler tekliflerde otomatik tamamlama olarak çıkar.</p>
        </div>
      </div>
      <div className="catalog-actions">
        <button className="primary-button" onClick={openNew}><Plus size={15} /> Ürün ekle</button>
        <button data-testid="download-template-button" className="outline-button" onClick={downloadTemplate}>
          <FileSpreadsheet size={15} /> Şablon indir
        </button>
        <label className="outline-button">
          <Upload size={15} /> Excel yükle
          <input data-testid="import-excel-input" type="file" accept=".xlsx,.xls,.csv" onChange={importExcel} hidden />
        </label>
        <button data-testid="export-catalog-button" className="outline-button" onClick={exportCatalog}>
          <Download size={15} /> Katalogu indir
        </button>
        <div className="search-box catalog-search">
          <Search size={16} />
          <input data-testid="catalog-search-input" placeholder="Katalogda ara"
            value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="empty-state">
          <Package size={30} />
          <h2>Katalog boş</h2>
          <p>Ürün ekle düğmesini kullanın veya Excel ile yükleyin.</p>
        </div>
      ) : (
        <div className="catalog-list">
          <div className="catalog-row catalog-head">
            <span>Görsel</span><span>Ürün adı</span><span>Stok kodu</span><span>Fiyat</span><span>Son güncelleme</span><span></span>
          </div>
          {filtered.map(p => (
            <div className="catalog-row" key={p.name} data-testid={`catalog-row-${p.name}`}>
              <label className="catalog-thumb">
                {p.image_url ? <img src={p.image_url} alt={p.name} /> : <ImageIcon size={16} />}
                <input type="file" accept="image/*" hidden
                  onChange={e => uploadProductImage(p.name, e.target.files?.[0])} />
              </label>
              <span>{p.name}</span>
              <input data-testid={`catalog-sku-${p.name}`} className="catalog-sku-input"
                defaultValue={p.sku || ""} placeholder="SKU"
                onBlur={e => { if (e.target.value !== (p.sku || "")) updateSku(p.name, e.target.value); }} />
              <span data-testid={`catalog-price-${p.name}`}>{money(p.default_price, { TRY: "₺", EUR: "€", GBP: "£" }[p.price_currency] || "$")}</span>
              <span>{p.updated_at ? new Date(p.updated_at).toLocaleDateString("tr-TR") : "-"}</span>
              <div><button className="icon-button" title="Düzenle" onClick={() => setSelected({ ...p, originalName: p.name })}><Pencil size={15} /></button>
              <button className="icon-button" title="Sil" onClick={() => removeProduct(p.name)}
                data-testid={`delete-product-${p.name}`}>
                <Trash2 size={15} />
              </button></div>
            </div>
          ))}
        </div>
      )}
      {selected && <div className="modal-overlay" onClick={() => setSelected(null)}>
        <div className="modal-card lg" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setSelected(null)}><X size={16} /></button>
          <h2>{selected.originalName ? "Ürünü düzenle" : "Ürün ekle"}</h2>
          <div className="settings-grid">
            <label>Ürün adı<input value={selected.name} onChange={e => setSelected({ ...selected, name: e.target.value })} /></label>
            <label>Stok kodu (SKU)<input value={selected.sku || ""} onChange={e => setSelected({ ...selected, sku: e.target.value })} /></label>
            <label>Liste fiyatı<input type="number" min="0" step="0.01" value={selected.default_price} onFocus={e => e.target.select()} onChange={e => setSelected({ ...selected, default_price: e.target.value })} /></label>
            <label>Para birimi<select value={selected.price_currency} onChange={e => setSelected({ ...selected, price_currency: e.target.value })}><option value="USD">USD</option><option value="TRY">TL</option><option value="EUR">EUR</option><option value="GBP">GBP</option></select></label>
          </div>
          <label className="catalog-image-picker">Ürün görseli
            <span>{selected.image_url ? <img src={selected.image_url} alt="Ürün önizleme" /> : <ImageIcon size={26} />}</span>
            <input type="file" accept="image/*" onChange={e => uploadSelectedImage(e.target.files?.[0])} />
          </label>
          <div className="settings-actions"><button className="outline-button" onClick={() => setSelected(null)}>Vazgeç</button><button className="primary-button" disabled={saving} onClick={saveProduct}><Save size={15} /> Kaydet</button></div>
        </div>
      </div>}
    </section>
  );
}
